'use strict'

/**
 * マトリクス画面の「ほぼ変化しない土台データ」を一定時間メモリにキャッシュするモジュール。 対象は setting.json（グラフ定義）と CHROFY 項目一覧（AppMaster 由来）の 2 つで、
 * 初回表示で取得した後、TTL（30 分）以内に画面を離れて戻ってきても再取得せずキャッシュを使う。 TTL を過ぎた最初のアクセスで再取得し、キャッシュを更新する。
 *
 * モジュールレベルの変数で保持するため、コンポーネントのアンマウント/再マウント（＝フィーチャーストアの作り直し）を またいでも保持される。マッピング状況 API はキャッシュ対象外で、従来どおり毎回取得する。
 * 成功した結果のみキャッシュし、失敗（throw）はキャッシュしない（呼び出し元のフォールバックに委ねる）。
 */

import { fetchSettingJson, fetchAllChrofyColumns } from './services'

/** キャッシュ有効期間（ミリ秒）: 30 分 */
export const MATRIX_DATA_CACHE_TTL_MS = 30 * 60 * 1000

/** @type {{ value: import('./types').SettingJson; expiresAt: number } | null} */
let settingCache = null
/** @type {Map<string, { value: import('./types').ChrofyColumnsResponse; expiresAt: number }>} */
const columnsCacheByCorp = new Map()

/**
 * キャッシュエントリが有効（未期限切れ）かどうか。
 *
 * @param {{ expiresAt: number } | null | undefined} entry
 * @returns {boolean}
 */
function isValid(entry) {
  return entry != null && entry.expiresAt > Date.now()
}

/**
 * Setting.json をキャッシュ付きで取得。TTL 内はキャッシュ値を返し、API/S3 を呼ばない。
 *
 * @returns {Promise<import('./types').SettingJson>}
 */
export async function fetchSettingJsonCached() {
  if (isValid(settingCache)) return settingCache.value
  const value = await fetchSettingJson()
  settingCache = { value, expiresAt: Date.now() + MATRIX_DATA_CACHE_TTL_MS }
  return value
}

/**
 * CHROFY 項目一覧をキャッシュ付きで取得（法人番号ごとに保持）。TTL 内はキャッシュ値を返す。
 *
 * @param {string} corporationNumber
 * @returns {Promise<import('./types').ChrofyColumnsResponse>}
 */
export async function fetchAllChrofyColumnsCached(corporationNumber) {
  const key = corporationNumber || ''
  const entry = columnsCacheByCorp.get(key)
  if (isValid(entry)) return entry.value
  const value = await fetchAllChrofyColumns(corporationNumber)
  columnsCacheByCorp.set(key, { value, expiresAt: Date.now() + MATRIX_DATA_CACHE_TTL_MS })
  return value
}

/** キャッシュを全消去する（主にテストの分離用）。 */
export function _clearMatrixDataCache() {
  settingCache = null
  columnsCacheByCorp.clear()
}
